import { createApi } from '@reduxjs/toolkit/query/react';
import { baseApiQuery } from '../basequery';

export type FormEntitySectionKey =
  | 'client.business_names'
  | 'client.business_contacts'
  | 'client.client_attorney'
  | 'client.private_equity_ownership'
  | 'opposite_side.opposite_sides'
  | 'opposite_side.contacts'
  | 'opposite_side.attorney'
  | 'opposite_side.private_equity_ownership';

export interface SaveFormEntityPayload {
  sectionKey: FormEntitySectionKey;
  entityType: 'Business' | 'Individual';
  entityName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  emailAddress?: string;
  country: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  coiEntityId?: number;
  formAddressId?: number;
}

export interface SaveFormEntityResponse {
  data: {
    formId: number;
    coiEntityId: number;
    formAddressId: number;
    entityTypeId: number;
    entitySectionId: number;
  };
}

export interface FormEntityRecord {
  coiEntityId: number;
  formAddressId: number;
  entityTypeId: number;
  entitySectionId: number;
  sectionKey: FormEntitySectionKey;
  entityType: 'Business' | 'Individual';
  entityName: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  emailAddress: string | null;
  country: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
}

export interface GetFormEntitiesResponse {
  data: {
    formId: number;
    entities: FormEntityRecord[];
  };
}

export const entityApi = createApi({
  reducerPath: 'entityApi',
  baseQuery: baseApiQuery,
  tagTypes: ['FormEntities'],
  endpoints: (builder) => ({
    getFormEntities: builder.query<GetFormEntitiesResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/entities/forms/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, { id }) => [{ type: 'FormEntities', id }],
    }),
    saveFormEntity: builder.mutation<
      SaveFormEntityResponse,
      { id: number; data: SaveFormEntityPayload }
    >({
      query: ({ id, data }) => ({
        url: `/entities/forms/${id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'FormEntities', id }],
    }),
    deleteEntity: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/entities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormEntities'],
    }),
    deleteEntities: builder.mutation<void, { ids: number[] }>({
      query: ({ ids }) => ({
        url: `/entities`,
        params: { ids },
        method: 'DELETE',
      }),
      invalidatesTags: ['FormEntities'],
    }),
  }),
});

export const {
  useGetFormEntitiesQuery,
  useSaveFormEntityMutation,
  useDeleteEntityMutation,
  useDeleteEntitiesMutation,
} = entityApi;
